import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import PropTypes from "prop-types";
import React, { cloneElement, useEffect } from "react";
import InputMask from "react-input-mask";

import { uniqueKey } from "@utils/react";

const useStyles = makeStyles(() => ({
  main: {},
  searcher: {},
  searcherField: {},
  searcherInput: {
    marginTop: "24px",
  },
  searcherControl: {
    marginTop: "24px",
    textAlign: "right",
  },
  searcherControlClearMode: {
    marginTop: "24px",
    textAlign: "right",
  },
  searcherClearButton: {
    marginRight: "10px",
  },
  selectOption: {
    display: "flex",
  },
  "@media all and (min-width: 768px)": {
    searcher: {
      display: "flex",
      alignItems: "center",
    },
    searcherField: {
      minWidth: "160px",
    },
    searcherInput: {
      minWidth: "240px",
      marginLeft: "24px",
      marginTop: "0px",
    },
    searcherControl: {
      marginLeft: "24px",
      marginTop: "0px",
    },
    searcherControlClearMode: {
      marginLeft: "0px",
      marginTop: "0px",
    },
    searcherClearButton: {
      marginLeft: "10px",
      marginRight: "10px",
    },
    selectOption: {
      display: "flex",
    },
  },
}));

function Filter(props) {
  const classes = useStyles();

  const [filtered, setFiltered] = React.useState(false);
  const [field, setField] = React.useState(props.fields[0]);
  const [input, setInput] = React.useState("");

  function findField(fieldName) {
    for (const field of props.fields) {
      if (field.name === fieldName) {
        return field;
      }
    }
    return null;
  }

  function filterStartsWithText() {
    let filterValue = input.toUpperCase();
    if (field.render) {
      filterValue = field.render(filterValue);
    }

    const filteredRows = props.rows.filter((row) => {
      const rowValue = row[field.name].toUpperCase();
      return rowValue.startsWith(filterValue);
    });
    setFiltered(true);
    props.onFilter(filteredRows);
  }

  function filterContainsText() {
    let filterValue = input.toUpperCase();
    if (field.render) {
      filterValue = field.render(filterValue);
    }

    const filteredRows = props.rows.filter((row) => {
      const rowValue = row[field.name].toUpperCase();
      return rowValue.includes(filterValue);
    });
    setFiltered(true);
    props.onFilter(filteredRows);
  }

  function filterSelect() {
    const filteredRows = props.rows.filter((row) => {
      return row[field.name] === input;
    });
    setFiltered(true);
    props.onFilter(filteredRows);
  }

  function filter() {
    if (input === "") {
      clear();
    } else if (typeof field.type === "undefined" || field.type === "text") {
      if (typeof field.action === "undefined" || field.action === "contains") {
        filterContainsText();
      } else if (field.action === "starts_with") {
        filterStartsWithText();
      } else {
        throw new Error("Invalid field action");
      }
    } else if (field.type === "select") {
      filterSelect();
    } else {
      throw new Error("Invalid field type");
    }
  }

  function clear() {
    setFiltered(false);
    setInput("");
    props.onFilter(props.rows);
  }

  function handleChangeField(e) {
    const field = findField(e.target.value);
    if (field) {
      clear();
      setField(field);
    } else {
      // ignore exception situation.
    }
  }

  function handleChangeInput(e) {
    setInput(e.target.value);
  }

  function handleFilter() {
    filter();
  }

  function handleClear() {
    clear();
  }

  useEffect(() => {
    if (filtered) {
      filter();
    } else {
      props.onFilter(props.rows);
    }
  }, [props.rows]);

  return (
    <div className={classes.main}>
      <div className={classes.searcher}>
        <div className={classes.searcherField}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="field-select"></InputLabel>
            <Select
              labelId="field-select"
              value={field.name}
              onChange={handleChangeField}
            >
              {props.fields.map((field) => (
                <MenuItem key={uniqueKey()} value={field.name}>
                  {field.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={classes.searcherInput}>
          {field.type === "text" && typeof field.mask === "undefined" && (
            <TextField
              value={input}
              label={field.label}
              variant="outlined"
              fullWidth
              onChange={handleChangeInput}
            />
          )}
          {field.type === "text" && typeof field.mask === "string" && (
            <InputMask
              mask={field.mask}
              maskChar=" "
              value={input}
              onChange={handleChangeInput}
              variant="outlined"
              fullWidth
              label={field.label}
            >
              {(props) => <TextField {...props} />}
            </InputMask>
          )}
          {field.type === "select" && (
            <FormControl fullWidth variant="outlined">
              <InputLabel>{field.label}</InputLabel>
              <Select
                variant="outlined"
                fullWidth
                value={input}
                label={field.label}
                onChange={handleChangeInput}
              >
                {field.options.map((option) => (
                  <MenuItem
                    key={`filter-select-${option.value}`}
                    value={option.value}
                  >
                    <div className={classes.selectOption}>
                      {option.icon && (
                        <ListItemIcon>
                          {cloneElement(option.icon, {
                            fontSize: "small",
                          })}
                        </ListItemIcon>
                      )}
                      <span>{option.label}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div
          className={
            filtered
              ? classes.searcherControlClearMode
              : classes.searcherControl
          }
        >
          {filtered && (
            <IconButton
              className={classes.searcherClearButton}
              onClick={handleClear}
            >
              <ClearIcon />
            </IconButton>
          )}
          <Button onClick={handleFilter} variant="contained" color="primary">
            Filtrar
          </Button>
        </div>
      </div>
      <div></div>
    </div>
  );
}

Filter.defaultProps = {
  rows: [],
  fields: [],
  onFilter: () => {},
};

Filter.propTypes = {
  rows: PropTypes.array.isRequired,
  fields: PropTypes.array.isRequired,
  onFilter: PropTypes.func.isRequired,
};

export default Filter;
